const STORAGE_KEY = 'credlap.auth.session';

interface StoredSession {
  token: string;
  expiresAt: number;
}

export class AuthStorage {
  // Auditoría de robustez 2026-07-06: `readToken()` corre en el inicializador del signal `_token`
  // de AuthService — sin este try/catch, un acceso a localStorage que tira (Safari con "Bloquear
  // todas las cookies", política corporativa que deshabilita Web Storage) rompía el bootstrap
  // entero de la app con una pantalla en blanco, no solo esta función puntual.
  static readToken(now: number = Date.now()): string | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const session = parseSession(raw);
      if (!session || session.expiresAt <= now) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return session.token;
    } catch {
      return null;
    }
  }

  static writeToken(token: string, expiresInSeconds: number, now: number = Date.now()): void {
    try {
      const session: StoredSession = { token, expiresAt: now + expiresInSeconds * 1000 };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      // La sesión sigue funcionando en memoria para esta pestaña (AuthService.token() ya tiene
      // el valor); solo no persiste entre recargas, igual que ThemeService y el período del
      // dashboard en el mismo caso.
    }
  }

  static clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nada que limpiar si el storage nunca escribió nada — no debe romper el logout.
    }
  }
}

function parseSession(raw: string): StoredSession | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).token === 'string' &&
      typeof (parsed as StoredSession).expiresAt === 'number'
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    return null;
  }
}
